import { useState } from 'preact/hooks';
import { type APIHistoryItem, isPending, qrContent } from '../core';
import { formatCW, formatDate, formatNumber, relativeTime } from '../app/format';
import { model } from '../app/model';
import { Prefs } from '../app/prefs';
import { BrandMark, BrandWordmark, PlainButton, PrimaryButton, QRCodeView, SecondaryButton, Sheet, Spinner, copyText, haptic } from './components';
import { ArrowDown, ArrowUp, Check, Copy, Eye, EyeOff, QRScan, Share, Warning, Wifi } from './icons';
import { shareOrCopy } from './onboarding';
import { ui, useApp } from './state';

export function HomeView() {
  const { s, model } = useApp();
  const snap = model.snapshot;
  const [hide, setHide] = useState(Prefs.hideBalance());
  const [copied, setCopied] = useState(false);
  const backupDone = Prefs.backupDone();
  const items = snap?.history.slice(0, 4) ?? [];

  return (
    <div class="tab-page">
      <header class="home-header">
        <BrandMark size={34} />
        <BrandWordmark width={132} />
      </header>

      <div class="card balance">
        <button type="button" class="plain balance-label" onClick={() => { Prefs.setHideBalance(!hide); setHide(!hide); }}
          aria-label={hide ? s.showBalance : s.hideBalance}>
          <span class="section-title">{s.totalBalance}</span>
          {hide ? <EyeOff size={15} /> : <Eye size={15} />}
        </button>
        {snap ? (
          <>
            <div class="balance-amount">{hide ? '•••••• CW' : formatCW(snap.total)}</div>
            {snap.unconfirmed !== 0 && !hide && <div class="dim small">🕓 {s.pendingAmount(formatCW(snap.unconfirmed, true))}</div>}
          </>
        ) : <div class="balance-loading"><Spinner /></div>}
      </div>

      <div class="actions">
        <ActionButton title={s.send} disabled={!snap} onClick={() => ui.open({ type: 'send' })}><ArrowUp /></ActionButton>
        <ActionButton title={s.receive} disabled={!snap} onClick={() => ui.open({ type: 'receive' })}><ArrowDown /></ActionButton>
        <ActionButton title={s.scan} onClick={() => ui.setTab('scan')}><QRScan /></ActionButton>
      </div>

      {!backupDone && (
        <button type="button" class="reminder" onClick={() => void startBackupFromSettings()}>
          <span class="icon-circle"><Warning size={18} strokeWidth={2.5} /></span>
          <span class="grow">
            <span class="strong block">{s.backupReminderTitle}</span>
            <span class="dim small">{s.backupReminderText}</span>
          </span>
          <span class="dim">›</span>
        </button>
      )}

      {model.errorMessage && (
        <div class="card pad error-card"><Wifi size={18} /> <span class="small">{model.errorMessage}</span></div>
      )}

      <div class="card">
        <div class="section-title pad-h pad-top">{s.latestTransactions}</div>
        {!snap ? <div class="pad center"><Spinner /></div>
          : items.length === 0 ? <p class="dim pad">{s.noTransactions}</p>
          : (
            <>
              {items.map((it, i) => (
                <>
                  <HistoryRow item={it} onClick={() => ui.open({ type: 'tx', item: it })} />
                  {i < items.length - 1 && <div class="divider inset" />}
                </>
              ))}
              <div class="divider" />
              <button type="button" class="see-all" onClick={() => ui.setTab('activity')}>{s.seeAll}</button>
            </>
          )}
      </div>

      <div class="card pad">
        <div class="section-title">{s.myAddress}</div>
        {snap ? (
          <div class="address-row">
            <span class="mono small ellipsis-middle">{snap.receiveAddress.address.string}</span>
            <button type="button" class="glass-circle" aria-label={s.copyAddress} onClick={async () => {
              await copyText(snap.receiveAddress.address.string).catch(() => {});
              haptic();
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            }}>{copied ? <Check size={18} /> : <Copy size={18} />}</button>
          </div>
        ) : <Spinner />}
      </div>
    </div>
  );
}

/** Opens the backup screen after authentication (reminder banner and Settings). */
export async function startBackupFromSettings() {
  const phrase = await model.revealPhrase();
  if (phrase) backupState.open(phrase);
}

/** Small store for the "backup from settings" sheet. */
export const backupState = {
  phrase: null as string | null,
  open(p: string) {
    this.phrase = p;
    ui.dispatchEvent(new Event('change'));
  },
  close() {
    this.phrase = null;
    ui.dispatchEvent(new Event('change'));
  },
};

function ActionButton({ title, onClick, disabled, children }: { title: string; onClick: () => void; disabled?: boolean; children: preact.ComponentChildren }) {
  return (
    <button type="button" class="glass-tile action" disabled={disabled} onClick={onClick}>
      <span class="icon-circle big">{children}</span>
      <span class="action-label">{title}</span>
    </button>
  );
}

export function HistoryRow({ item, onClick }: { item: APIHistoryItem; onClick?: () => void }) {
  const { s } = useApp();
  const incoming = item.net >= 0;
  return (
    <button type="button" class="history-row" onClick={onClick}>
      <span class="icon-circle">{incoming ? <ArrowDown size={16} strokeWidth={2.6} /> : <ArrowUp size={16} strokeWidth={2.6} />}</span>
      <span class="grow">
        <span class="strong block">{incoming ? s.received : s.sent}</span>
        <span class="dim small">{isPending(item) ? s.pending : s.confirmed}</span>
      </span>
      <span class="text-right">
        <span class="strong block tabular">{formatCW(item.net, true)}</span>
        <span class="dim small">{item.time > 0 ? relativeTime(item.time) : s.now}</span>
      </span>
    </button>
  );
}

export function ReceiveSheet() {
  const { s, model } = useApp();
  const [copied, setCopied] = useState(false);
  const addr = model.snapshot?.receiveAddress.address.string;
  return (
    <Sheet title={s.receiveTitle} right={<PlainButton onClick={() => ui.close()}>{s.ok}</PlainButton>}>
      {addr ? (
        <div class="stack-24 center-col">
          <div class="qr-card"><QRCodeView text={qrContent(addr)} label={s.qrLabel} /></div>
          <p class="mono text-center break selectable">{addr}</p>
          <div class="two-buttons">
            <PrimaryButton onClick={async () => { await copyText(addr).catch(() => {}); setCopied(true); }}>
              {copied ? <Check size={18} /> : <Copy size={18} />} {copied ? s.copied : s.copy}
            </PrimaryButton>
            <SecondaryButton onClick={() => shareOrCopy(addr)}><Share size={18} /> {s.share}</SecondaryButton>
          </div>
          <p class="dim small text-center">{s.receiveOnlyCW}</p>
          <p class="dimmer small text-center">{s.receiveNewAddress}</p>
        </div>
      ) : <Spinner />}
    </Sheet>
  );
}

export function TxDetailSheet({ item }: { item: APIHistoryItem }) {
  const { s } = useApp();
  const [copied, setCopied] = useState(false);
  const incoming = item.net >= 0;
  return (
    <Sheet title={s.transaction} right={<PlainButton onClick={() => ui.close()}>{s.ok}</PlainButton>}>
      <div class="center-col stack-10 tx-hero">
        <span class="icon-circle huge">{incoming ? <ArrowDown size={24} strokeWidth={2.6} /> : <ArrowUp size={24} strokeWidth={2.6} />}</span>
        <span class="dim">{incoming ? s.received : s.sent}</span>
        <span class="amount-big">{formatCW(item.net, true)}</span>
      </div>
      <div class="group-body">
        <div class="row"><span class="row-label">{s.status}</span><span class="row-value">{isPending(item) ? s.pending : s.confirmations(item.confirmations)}</span></div>
        {item.time > 0 && <div class="row"><span class="row-label">{s.date}</span><span class="row-value">{formatDate(new Date(item.time * 1000))}</span></div>}
        {item.height !== null && <div class="row"><span class="row-label">{s.block}</span><span class="row-value">{formatNumber(item.height)}</span></div>}
        {item.net < 0 && item.fee !== null && <div class="row"><span class="row-label">{s.networkFee}</span><span class="row-value tabular">{formatCW(item.fee)}</span></div>}
      </div>
      <div class="group-title">{s.transactionID}</div>
      <div class="group-body pad">
        <p class="mono small break selectable">{item.txid}</p>
        <PlainButton class="small strong" onClick={async () => { await copyText(item.txid).catch(() => {}); setCopied(true); }}>
          {copied ? <Check size={16} /> : <Copy size={16} />} {copied ? s.copied : s.copy}
        </PlainButton>
      </div>
    </Sheet>
  );
}
